import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import BreadcrumbsCustom from 'src/components/common/BreadcrumbsCustom';
import InputCustom from 'src/components/common/field/InputCustom';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import DropDownStatus from 'src/components/master/DropDownStatus';
import Record from 'src/components/master/Record';
import DialogDetail from 'src/components/master/productOP/DialogDetail';
import DialogForm from 'src/components/master/productOP/DialogForm';

import Pagin, { PaginationType } from 'src/components/common/Pagin';
import { postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import { getField } from 'src/common/helper';
import { FieldTableType } from 'src/components/master/TypeCustom';

const breadcrumbsDefine = [
  {
    to: '/',
    label: '会計'
  },
  {
    label: '手術材料管理一覧'
  }
];

const defaultFilter = {
  status: 1,
  product: null
};

const paginationDefault = {
  first: false,
  formatTotal: '0',
  last: false,
  page: 1,
  pages: 1,
  pagesize: 20,
  total: 0
};


const fieldsDefine: FieldTableType[] = [
  {
    label: '材料名',
    field: 'product',
    align: 'left',
    width: '40%'
  },
  {
    label: 'ベンダー',
    field: 'vendor',
    align: 'left',
    width: '30%'
  },
  {
    label: '最低在庫量',
    field: 'minstock',
    align: 'right',
    width: '20%'
  },
  {
    label: '単位',
    field: 'unit',
    align: 'left',
    width: '10%'
  }
];

type DataDialogForm = {
  datas: any | undefined;
  open: boolean;
};

type FilterType = {
  status: number | null;
  product: string | null;
};

const MasterSurgicalMaterials = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>(defaultFilter);
  const [datas, setDatas] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationType>(paginationDefault);
  const [dataDialogDetail, setdataDialogDetail] = useState({
    id: 0,
    open: false
  });

  const [dataDialogForm, setdataDialogForm] = useState<DataDialogForm>({
    datas: undefined,
    open: false
  });

  const handleClickForm = (data: any | undefined = undefined) => {
    handleCloseDetail();
    setdataDialogForm({
      datas: data,
      open: true
    });
  };

  const handleCloseForm = (isRefreshList: boolean = false) => {
    if (isRefreshList) {
      getList();
    }
    setdataDialogForm({
      datas: undefined,
      open: false
    });
  };

  const handleClickDetail = (data: any | undefined) => {
    setdataDialogDetail({
      id: getField(data, 'id', 0),
      open: true
    });
  };

  const handleCloseDetail = () => {
    setdataDialogDetail({
      id: 0,
      open: false
    });
  };

  const handleChangeFilter = (name: string) => (id: number | string | null) => {
    setFilter(prevState => ({
      ...prevState,
      [name]: id !== '' ? id : null
    }))
  };

  const handlePageChange = (eventL: any, value: number) => {
    getList(value);
  };

  const handleSearch = () => {
    getList();
  };

  const getList = (page?: number) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        ...filter,
        page: page ? page : pagination.page,
        pagesize: pagination.pagesize
      };

      setIsLoading(true);
      postAPI(`/master/product-op-search?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setDatas(getField(res.data, 'items', []));
            setPagination(getField(res.data, 'pagination', {}));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  useEffect(() => {
    getList();

    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <BreadcrumbsCustom breadcrumbs={breadcrumbsDefine} />
      <Typography
        component='label'
        sx={{
          fontWeight: '500',
          fontSize: '20px',
          lineHeight: '24px'
        }}
      >
        {'手術材料管理'}
      </Typography>

      <Stack
        direction={'row'}
        spacing='8px'
        sx={{
          mt: '18px'
        }}
      >
        <Stack
          direction={'row'}
          spacing='8px'
        >
          {!isLoading ? (
            <InputCustom
              name='product'
              value={filter.product}
              onChange={handleChangeFilter('product')}
              type='text'
              labelInput='材料名'
              sx={{ width: '320px', height: '32px', minHeight: '32px' }}
              sxInput={{ height: '0em' }}
            />
          ) : (
            <Skeleton variant='rectangular' width={'320px'} height={'32px'} />
          )}

          {!isLoading ? (
            <DropDownStatus
              selected={filter.status}
              onChange={handleChangeFilter('status')}
            />
          ) : (
            <Box width={'160px'}>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!isLoading ? (
            <BtnGradient
              text='検索'
              handleButtonClick={handleSearch}
              sx={{
                minWidth: '60px',
                width: '60px',
              }}
            />
          ) : (
            <Box width={'60px'}>
              <Skeleton variant='rectangular' width={'60px'} height={'32px'} />
            </Box>
          )}
        </Stack>

        <Stack
          direction={'row'}
          justifyContent={'right'}
          width={'100%'}
        >
          {!isLoading ? (
            <BtnOutlineGradient
              text='新規作成'
              srcStartIcon='/images/ic-plus-blue.svg'
              handleButtonClick={() => handleClickForm()}
              sx={{
                width: '120px'
              }}
            />
          ) : (
            <Box width={'120px'}>
              <Skeleton variant='rectangular' width={'120px'} height={'32px'} />
            </Box>
          )}
        </Stack>
      </Stack>

      <Box sx={{
        mt: '27px'
      }}>
        {!isLoading ? (
          <Record
            items={datas}
            fields={fieldsDefine}
            isLoading={isLoading}
            onClickRow={handleClickDetail}
          />
        ) : (
          <Skeleton variant='rounded' width={'100%'} height={'calc(100vh - 320px)'} />
        )}
      </Box>
      {datas.length > 0 && pagination && pagination.page && (
        <Pagin handlePageChange={handlePageChange} pagination={pagination} />
      )}

      <DialogDetail
        open={dataDialogDetail.open}
        id={dataDialogDetail.id}
        onClose={handleCloseDetail}
        onClickEdit={handleClickForm}
      />

      <DialogForm
        open={dataDialogForm.open}
        datas={dataDialogForm.datas}
        onClose={handleCloseForm}
      />
    </Box>
  );
}

export default MasterSurgicalMaterials;